import {
  AccountType,
  ArbitrationPayment,
  Dispute,
  DisputeEvidence,
  DisputeReasonCode,
  DisputeStage,
  DisputeStatus,
  EscrowParticipant,
  EscrowPayoutComponentStatus,
  EscrowPayoutRoleSummary,
  EscrowPayouts,
  PaymentOrder,
  PaymentOrderMetadata,
  PaymentOrderStatus,
  PaymentStatus,
  PayoutAccount,
  PayoutChannel,
  PayoutComponent,
  PayoutStatus,
  TradeEscrow,
  TradeEscrowMetadata,
} from './types';

type RawAccountType = {
  id: number;
  slug: string;
  label: string;
  channel_category: string;
  channel_code: string | null;
};

export function adaptAccountType(raw: RawAccountType): AccountType {
  return {
    id: raw.id,
    slug: raw.slug,
    label: raw.label,
    channelCategory: raw.channel_category,
    channelCode: raw.channel_code,
  };
}

type RawPayoutChannel = {
  channel_code: string;
  channel_name: string;
  channel_category: string;
  currency: string;
  min_amount: number | null;
  max_amount: number | null;
};

export function adaptPayoutChannel(raw: RawPayoutChannel): PayoutChannel {
  return {
    channelCode: raw.channel_code,
    channelName: raw.channel_name,
    channelCategory: raw.channel_category,
    currency: raw.currency,
    minAmount: raw.min_amount,
    maxAmount: raw.max_amount,
  };
}

type RawPayoutAccount = {
  id: number;
  user_id: number;
  account_type?: RawAccountType;
  account_type_id?: number;
  account_name: string;
  account_number: string;
  channel_code: string | null;
  driver: string;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
};

export function adaptPayoutAccount(raw: RawPayoutAccount): PayoutAccount {
  return {
    id: raw.id,
    userId: raw.user_id,
    accountTypeId: raw.account_type_id ?? raw.account_type?.id,
    accountType: raw.account_type ? adaptAccountType(raw.account_type) : undefined,
    accountName: raw.account_name,
    accountNumber: raw.account_number,
    channelCode: raw.channel_code,
    driver: raw.driver,
    isVerified: raw.is_verified,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

type RawPaymentOrderMetadata = {
  purchase_price?: number;
  escrow_deposit?: number;
  purchase_price_remainder?: number;
};

function adaptPaymentOrderMetadata(raw: RawPaymentOrderMetadata): PaymentOrderMetadata {
  return {
    purchasePrice: raw.purchase_price,
    escrowDeposit: raw.escrow_deposit,
    purchasePriceRemainder: raw.purchase_price_remainder,
  };
}

type RawPaymentOrder = {
  id: string;
  code: string;
  user_id: number;
  description: string;
  amount: string;
  amount_paid: string;
  currency: string;
  status: PaymentOrderStatus;
  payment_method?: string | null;
  payment_driver?: string | null;
  payment_held_at?: string | null;
  metadata: RawPaymentOrderMetadata | null;
};

export function adaptPaymentOrder(raw: RawPaymentOrder): PaymentOrder {
  return {
    id: raw.id,
    code: raw.code,
    userId: raw.user_id,
    description: raw.description,
    amount: raw.amount,
    amountPaid: raw.amount_paid,
    currency: raw.currency,
    status: raw.status,
    paymentMethod: raw.payment_method ?? null,
    paymentDriver: raw.payment_driver ?? null,
    paymentHeldAt: raw.payment_held_at ?? null,
    metadata: raw.metadata ? adaptPaymentOrderMetadata(raw.metadata) : {},
  };
}

type RawEscrowParticipant = {
  id: number;
  username: string;
};

function adaptEscrowParticipant(raw: RawEscrowParticipant): EscrowParticipant {
  return {
    id: raw.id,
    username: raw.username,
  };
}

type RawTradeEscrowMetadata = {
  buyer_payment_url: string | null | undefined;
  seller_payment_url: string | null | undefined;
};

function adaptTradeEscrowMetadata(raw: RawTradeEscrowMetadata): TradeEscrowMetadata {
  return {
    buyerPaymentUrl: raw.buyer_payment_url ?? null,
    sellerPaymentUrl: raw.seller_payment_url ?? null,
  };
}

type RawDisputeEvidence = {
  id: string;
  dispute_id: string;
  uploaded_by_user_id: number;
  file_path?: string;
  file_name: string;
  file_type: string;
  file_size: number;
  description: string | null;
  file_url: string | null;
  thumbnail_url: string | null;
  created_at: string;
};

export function adaptDisputeEvidence(raw: RawDisputeEvidence): DisputeEvidence {
  return {
    id: raw.id,
    disputeId: raw.dispute_id,
    uploadedByUserId: raw.uploaded_by_user_id,
    filePath: raw.file_path,
    fileName: raw.file_name,
    fileType: raw.file_type,
    fileSize: raw.file_size,
    description: raw.description,
    fileUrl: raw.file_url,
    thumbnailUrl: raw.thumbnail_url,
    createdAt: raw.created_at,
  };
}

type RawDispute = {
  id: string;
  trade_escrow_id: string;
  opened_by_user_id: number;
  opened_by_role: 'buyer' | 'seller';
  reason_code: DisputeReasonCode;
  reason_text: string | null;
  status: DisputeStatus;
  stage: DisputeStage;
  triage_proposed_resolution: string | null;
  triage_accepted_by_buyer: boolean;
  triage_accepted_by_seller: boolean;
  assigned_arbitrator_id: number | null;
  decision_outcome: string | null;
  fault_party: 'buyer' | 'seller' | null;
  buyer_fault_percent: number | null;
  seller_fault_percent: number | null;
  decision_summary: string | null;
  arbitration_fee_buyer_funded: boolean;
  arbitration_fee_seller_funded: boolean;
  inspection_cost_buyer_funded: boolean;
  inspection_cost_seller_funded: boolean;
  funding_deadline: string | null;
  trade_escrow?: RawTradeEscrow;
  opened_by?: RawEscrowParticipant;
  evidence?: RawDisputeEvidence[];
  assigned_arbitrator?: RawEscrowParticipant | null;
  created_at: string;
};

export function adaptDispute(raw: RawDispute): Dispute {
  return {
    id: raw.id,
    tradeEscrowId: raw.trade_escrow_id,
    openedByUserId: raw.opened_by_user_id,
    openedByRole: raw.opened_by_role,
    reasonCode: raw.reason_code,
    reasonText: raw.reason_text,
    status: raw.status,
    stage: raw.stage,
    triageProposedResolution: raw.triage_proposed_resolution,
    triageAcceptedByBuyer: raw.triage_accepted_by_buyer,
    triageAcceptedBySeller: raw.triage_accepted_by_seller,
    assignedArbitratorId: raw.assigned_arbitrator_id,
    decisionOutcome: raw.decision_outcome,
    faultParty: raw.fault_party,
    buyerFaultPercent: raw.buyer_fault_percent,
    sellerFaultPercent: raw.seller_fault_percent,
    decisionSummary: raw.decision_summary,
    arbitrationFeeBuyerFunded: raw.arbitration_fee_buyer_funded,
    arbitrationFeeSellerFunded: raw.arbitration_fee_seller_funded,
    inspectionCostBuyerFunded: raw.inspection_cost_buyer_funded,
    inspectionCostSellerFunded: raw.inspection_cost_seller_funded,
    fundingDeadline: raw.funding_deadline,
    tradeEscrow: raw.trade_escrow ? adaptTradeEscrow(raw.trade_escrow) : undefined,
    openedBy: raw.opened_by ? adaptEscrowParticipant(raw.opened_by) : undefined,
    evidence: (raw.evidence ?? []).map(adaptDisputeEvidence),
    assignedArbitrator: raw.assigned_arbitrator != null ? adaptEscrowParticipant(raw.assigned_arbitrator) : null,
    createdAt: raw.created_at,
  };
}

// known component values; unrecognised strings map to 'unknown'
const KNOWN_PAYOUT_COMPONENTS: ReadonlySet<string> = new Set<PayoutComponent>([
  'buyer_deposit_forfeit',
  'buyer_deposit_return',
  'partial_refund',
  'partial_release',
  'purchase_price',
  'purchase_price_refund',
  'seller_deposit_forfeit',
  'seller_deposit_return',
]);

type RawPayoutComponentStatus = {
  component: string;
  failure_code: string | null;
  status: PayoutStatus;
  succeeded_at: string | null;
};

type RawPayoutRoleSummary = {
  components?: RawPayoutComponentStatus[];
  failure_code?: string | null;
  status: 'failed' | 'pending' | 'succeeded';
  succeeded_at?: string | null;
};

type RawPayouts = {
  buyer?: RawPayoutRoleSummary;
  seller?: RawPayoutRoleSummary;
};

/** Normalises a single payout component row from the API. */
function adaptPayoutComponent(raw: RawPayoutComponentStatus): EscrowPayoutComponentStatus {
  return {
    component: (KNOWN_PAYOUT_COMPONENTS.has(raw.component) ? raw.component : 'unknown') as PayoutComponent,
    failureCode: raw.failure_code,
    status: raw.status,
    succeededAt: raw.succeeded_at,
  };
}

/**
 * Normalises a role-level payout summary, applying the backward-compatibility
 * fallback: if `components` is missing or empty, synthesise a pseudo-component
 * from the role-level fields so downstream UI code always has a consistent shape.
 */
function adaptPayoutRoleSummary(raw: RawPayoutRoleSummary): EscrowPayoutRoleSummary {
  const components =
    raw.components && raw.components.length > 0
      ? raw.components.map(adaptPayoutComponent)
      : [
          {
            component: 'unknown' as PayoutComponent,
            failureCode: raw.failure_code ?? null,
            status: (raw.status === 'failed'
              ? 'failed'
              : raw.status === 'succeeded'
                ? 'succeeded'
                : 'pending') as PayoutStatus,
            succeededAt: raw.succeeded_at ?? null,
          },
        ];

  return { components, status: raw.status };
}

/** Normalises the top-level payouts object from the escrow status response. */
function adaptPayouts(raw?: RawPayouts): EscrowPayouts | undefined {
  if (!raw) return undefined;

  const result: EscrowPayouts = {};
  if (raw.buyer) result.buyer = adaptPayoutRoleSummary(raw.buyer);
  if (raw.seller) result.seller = adaptPayoutRoleSummary(raw.seller);

  return Object.keys(result).length > 0 ? result : undefined;
}

type RawTradeEscrow = {
  id: string;
  trade_product_id: number;
  order_product_id: number;
  buyer_id: number;
  seller_id: number;
  price: string;
  quantity: string;
  currency: string;
  buyer_escrow_deposit_amount: string;
  seller_escrow_deposit_amount: string;
  exchange_fee_buyer: string;
  exchange_fee_seller: string;
  delivery_window_end: string | null;
  metadata?: RawTradeEscrowMetadata;
  activated_at: string | null;
  delivered_at: string | null;
  accepted_at: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
  buyer?: RawEscrowParticipant;
  seller?: RawEscrowParticipant;
  payment_orders?: RawPaymentOrder[];
  payouts?: RawPayouts;
  disputes?: RawDispute[];
  counter_party?: { payment_status: PaymentOrderStatus } | null;
};

export function adaptTradeEscrow(raw: RawTradeEscrow): TradeEscrow {
  return {
    id: raw.id,
    tradeProductId: raw.trade_product_id,
    orderProductId: raw.order_product_id,
    buyerId: raw.buyer_id,
    sellerId: raw.seller_id,
    price: raw.price,
    quantity: raw.quantity,
    currency: raw.currency,
    buyerEscrowDepositAmount: raw.buyer_escrow_deposit_amount,
    sellerEscrowDepositAmount: raw.seller_escrow_deposit_amount,
    exchangeFeeBuyer: raw.exchange_fee_buyer,
    exchangeFeeSeller: raw.exchange_fee_seller,
    deliveryWindowEnd: raw.delivery_window_end,
    metadata: raw.metadata ? adaptTradeEscrowMetadata(raw.metadata) : { buyerPaymentUrl: null, sellerPaymentUrl: null },
    activatedAt: raw.activated_at,
    deliveredAt: raw.delivered_at,
    acceptedAt: raw.accepted_at,
    closedAt: raw.closed_at,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    buyer: raw.buyer ? adaptEscrowParticipant(raw.buyer) : { id: raw.buyer_id, username: '' },
    seller: raw.seller ? adaptEscrowParticipant(raw.seller) : { id: raw.seller_id, username: '' },
    paymentOrders: (raw.payment_orders ?? []).map(adaptPaymentOrder),
    payouts: adaptPayouts(raw.payouts),
    disputes: (raw.disputes ?? []).map(adaptDispute),
    counterParty: raw.counter_party ? { paymentStatus: raw.counter_party.payment_status } : null,
  };
}

type RawArbitrationPayment = {
  id: string;
  payment_order_id: number;
  code: string;
  amount: string;
  status: PaymentStatus;
  checkout_url: string | null;
  gateway_invoice_id: string | null;
};

export function adaptArbitrationPayment(raw: RawArbitrationPayment): ArbitrationPayment {
  return {
    id: raw.id,
    paymentOrderId: raw.payment_order_id,
    code: raw.code,
    amount: raw.amount,
    status: raw.status,
    checkoutUrl: raw.checkout_url,
    gatewayInvoiceId: raw.gateway_invoice_id,
  };
}
